import useSWR from 'swr';

import { fetchEndpointData } from '@utils/fetchEndpointData';
import type {
  TelemetryConnection,
  TelemetryLinkableTruck,
  TelemetryLinkableTrucksResponse,
  TelemetrySaveConnectionBody,
  TelemetryTruckTracking,
  TelemetryVehicle,
} from './endpoints/TelemetryEndpoints.types';
import type { TelemetryTrailerTracking } from './endpoints/TrailerTrackingEndpoints.types';

export type {
  TelemetryConnection,
  TelemetryLinkableTruck,
  TelemetryLinkableTrucksResponse,
  TelemetryTruckTracking,
  TelemetryTrailerTracking,
  TelemetryVehicle,
};

/**
 * Which party a page is acting for.
 *
 * The id is verified against the signed-in session server-side, so passing one the user has no access to
 * fails rather than leaking another carrier's connections. Omit it and the server uses the user's own
 * carrier.
 *
 * An internal user has no carrier of their own, so naming one is what narrows them to it — the backoffice
 * carrier page passes the carrier it is showing, which is how the backoffice sets that carrier's tracking up.
 */
export interface TelemetryParty {
  carrierId?: number;
}

/** Live-tracking refresh cadence, matching the order pages. */
export const LIVE_TRACKING_REFRESH_MS = 30_000;

/** Root of the connection list's SWR key. */
const TELEMETRY_CONNECTIONS_KEY = 'telemetry-connections';

/**
 * Matches the connection list's SWR key, whichever party it was fetched for.
 *
 * An action that only needs the list refreshed — the "add connection" dialog, say — hands this to
 * `useSWRConfig().mutate` and revalidates it without subscribing to data it never reads.
 */
export function isTelemetryConnectionsKey(key: unknown): boolean {
  return Array.isArray(key) && key[0] === TELEMETRY_CONNECTIONS_KEY;
}

/** Root of the SWR key of one connection's reported vehicles. */
const TELEMETRY_VEHICLES_KEY = 'telemetry-vehicles';

/**
 * Matches the reported-vehicles key of the connections one page is showing, for the party it is showing.
 *
 * These are the rows a card draws, and their links travel in them — so an action that re-points a unit to a
 * trailer or a truck has to revalidate them, not only the card it was taken from. Scoped by party on
 * purpose: the SWR cache outlives a route, and revalidating another carrier's keys would spend that
 * carrier's provider calls on a page nobody is looking at.
 */
export function isTelemetryVehiclesKey(key: unknown, party?: TelemetryParty): boolean {
  return Array.isArray(key) && key[0] === TELEMETRY_VEHICLES_KEY && key[2] === (party?.carrierId ?? null);
}

// The party travels in the query string, never the body: the endpoint wrapper validates the body
// against its Zod schema and replaces it with the parsed result *before* the permission check reads
// it, so an extra body field would already have been stripped by then.
function partyQuery(party: TelemetryParty | undefined, extra: Record<string, string | undefined> = {}): string {
  const params = new URLSearchParams();
  if (party?.carrierId !== undefined) {
    params.set('carrierId', String(party.carrierId));
  }
  for (const [key, value] of Object.entries(extra)) {
    if (value) {
      params.set(key, value);
    }
  }
  const query = params.toString();
  return query ? `?${query}` : '';
}

function partyFrom(carrierId: number | null): TelemetryParty {
  return carrierId !== null ? { carrierId } : {};
}

export async function getTelemetryConnections(party?: TelemetryParty): Promise<TelemetryConnection[]> {
  return fetchEndpointData<TelemetryConnection[]>(`/api/v1/telemetry/connections${partyQuery(party)}`);
}

export async function saveTelemetryConnection(
  body: TelemetrySaveConnectionBody,
  party?: TelemetryParty,
): Promise<TelemetryConnection> {
  return fetchEndpointData<TelemetryConnection>(`/api/v1/telemetry/connections${partyQuery(party)}`, {
    method: 'PUT',
    body,
  });
}

export async function deleteTelemetryConnection(id: string, party?: TelemetryParty): Promise<{ id: string }> {
  return fetchEndpointData<{ id: string }>(`/api/v1/telemetry/connections/${id}${partyQuery(party)}`, {
    method: 'DELETE',
  });
}

export async function verifyTelemetryConnection(id: string, party?: TelemetryParty): Promise<TelemetryConnection> {
  return fetchEndpointData<TelemetryConnection>(`/api/v1/telemetry/connections/${id}/verify${partyQuery(party)}`, {
    method: 'POST',
  });
}

/**
 * A connection's vehicles with their latest position.
 *
 * Refreshes from the provider before answering, which is what makes "fetch once on page load" work —
 * and what the live-tracking toggle then repeats on an interval.
 */
export async function getTelemetryVehicles(connectionId: string, party?: TelemetryParty): Promise<TelemetryVehicle[]> {
  return fetchEndpointData<TelemetryVehicle[]>(
    `/api/v1/telemetry/connections/${connectionId}/vehicles${partyQuery(party)}`,
  );
}

export async function linkTelemetryVehicle(
  vehicleId: string,
  target: { supplierTruckId: string } | { licensePlate: string } | { trailerId: number },
  party?: TelemetryParty,
): Promise<TelemetryVehicle> {
  return fetchEndpointData<TelemetryVehicle>(`/api/v1/telemetry/vehicles/${vehicleId}/link${partyQuery(party)}`, {
    method: 'POST',
    body: target,
  });
}

export async function unlinkTelemetryVehicle(vehicleId: string, party?: TelemetryParty): Promise<{ id: string }> {
  return fetchEndpointData<{ id: string }>(`/api/v1/telemetry/vehicles/${vehicleId}/link${partyQuery(party)}`, {
    method: 'DELETE',
  });
}

/**
 * The trucks the caller may link a vehicle to — what the linking dialog picks from.
 *
 * Linking by plate only works when some truck carries that plate. The live FleetMaster data reports a
 * plate no truck in the platform has, so without this the owner would be told the plate is unknown and
 * have no way to link at all.
 */
export async function getTelemetryLinkableTrucks(
  party?: TelemetryParty,
  search?: string,
): Promise<TelemetryLinkableTrucksResponse> {
  return fetchEndpointData<TelemetryLinkableTrucksResponse>(
    `/api/v1/telemetry/linkable-trucks${partyQuery(party, { search })}`,
  );
}

export async function getTruckTracking(
  supplierTruckId: string,
  party?: TelemetryParty,
): Promise<TelemetryTruckTracking> {
  return fetchEndpointData<TelemetryTruckTracking>(`/api/v1/telemetry/trucks/${supplierTruckId}${partyQuery(party)}`);
}

export function useTelemetryConnections(party?: TelemetryParty) {
  const carrierId = party?.carrierId ?? null;

  return useSWR([TELEMETRY_CONNECTIONS_KEY, carrierId], () => getTelemetryConnections(partyFrom(carrierId)));
}

/** `refreshInterval` is what the "start live tracking" toggle turns on; 0 keeps it a one-off fetch. */
export function useTelemetryVehicles(connectionId: string | undefined, party?: TelemetryParty, refreshInterval = 0) {
  const carrierId = party?.carrierId ?? null;

  return useSWR(
    connectionId ? [TELEMETRY_VEHICLES_KEY, connectionId, carrierId] : null,
    ([, id]) => getTelemetryVehicles(id, partyFrom(carrierId)),
    { refreshInterval },
  );
}

export function useTruckTracking(supplierTruckId: string | undefined, party?: TelemetryParty, refreshInterval = 0) {
  const carrierId = party?.carrierId ?? null;

  return useSWR(
    supplierTruckId ? ['telemetry-truck', supplierTruckId, carrierId] : null,
    ([, id]) => getTruckTracking(id, partyFrom(carrierId)),
    { refreshInterval },
  );
}

export async function getTrailerTracking(trailerId: number, party?: TelemetryParty): Promise<TelemetryTrailerTracking> {
  return fetchEndpointData<TelemetryTrailerTracking>(`/api/v1/telemetry/trailers/${trailerId}${partyQuery(party)}`);
}

/** A trailer's own panel, on the trailer page — the truck panel one link target over. */
export function useTrailerTracking(trailerId: number | undefined, party?: TelemetryParty, refreshInterval = 0) {
  const carrierId = party?.carrierId ?? null;

  return useSWR(
    trailerId ? ['telemetry-trailer', trailerId, carrierId] : null,
    ([, id]) => getTrailerTracking(id, partyFrom(carrierId)),
    { refreshInterval },
  );
}

/**
 * Every unit the caller's connections report, whichever connection reports it.
 *
 * The trailer page links a unit to the trailer it sits in, and which account that unit belongs to is not
 * something the user should have to know — they have one fleet, however many provider accounts it is
 * spread across. Fanned out inside the fetcher rather than as one hook per connection, because the number
 * of connections is not known until the list has loaded.
 */
async function getCompanyVehicles(party?: TelemetryParty): Promise<TelemetryVehicle[]> {
  const connections = await getTelemetryConnections(party);
  const perConnection = await Promise.all(connections.map((connection) => getTelemetryVehicles(connection.id, party)));

  return perConnection.flat();
}

/**
 * The whole fleet's reported units. Fetch it when a picker opens, not when a page loads: each connection
 * refreshes its snapshot from the provider when it answers, so an unused call spends the account's quota.
 */
export function useCompanyVehicles(party?: TelemetryParty) {
  const carrierId = party?.carrierId ?? null;

  return useSWR(['telemetry-company-vehicles', carrierId], () => getCompanyVehicles(partyFrom(carrierId)));
}

/** The trucks a vehicle may be linked to, filtered server-side as the user types in the picker. */
export function useLinkableTrucks(party?: TelemetryParty, search?: string) {
  const carrierId = party?.carrierId ?? null;

  return useSWR(['telemetry-linkable-trucks', carrierId, search ?? ''], () =>
    getTelemetryLinkableTrucks(partyFrom(carrierId), search),
  );
}
